import { useEffect, useState } from 'react';
import { ehNativo } from 'services/plataforma';

/**
 * Se o aparelho tem rede.
 *
 * <h2>Na web</h2>
 * `navigator.onLine` e os eventos `online`/`offline`. `false` é confiável: o navegador sabe quando
 * não há interface nenhuma no ar. `true` NÃO garante internet -- é só "há uma rede conectada", e um
 * Wi-Fi sem saída passa por isso. Por isto o hook serve para avisar, não para decidir: quem chama o
 * servidor continua tratando a própria falha, com a própria mensagem.
 *
 * <h2>No app</h2>
 * `navigator.onLine` NÃO serve. Na WebView do Android ele só reflete o estado real quando o app tem
 * a permissão ACCESS_NETWORK_STATE; sem ela a WebView assume "conectado" para sempre -- foi assim
 * que a tela de cadastro apareceu sem aviso nenhum em modo avião. O plugin @capacitor/network lê o
 * ConnectivityManager do sistema (e declara a permissão no manifesto dele, que o `cap sync` funde ao
 * do app). Import dinâmico atrás de `ehNativo()`, como os outros plugins do projeto: na web o
 * pacote nem entra no chunk.
 *
 * Sem `navigator` (Jest, SSR) assume online, que é o estado em que nada aparece.
 */
export function useOnline() {
	const [online, setOnline] = useState(() =>
		typeof navigator === 'undefined' || typeof navigator.onLine !== 'boolean' ? true : navigator.onLine
	);

	useEffect(() => {
		if (!ehNativo()) {
			const ficarOnline = () => setOnline(true);
			const ficarOffline = () => setOnline(false);
			window.addEventListener('online', ficarOnline);
			window.addEventListener('offline', ficarOffline);
			return () => {
				window.removeEventListener('online', ficarOnline);
				window.removeEventListener('offline', ficarOffline);
			};
		}

		// Mesmo desenho do useBotaoVoltarNativo: o registro é assíncrono, então a limpeza precisa
		// cobrir o caso de o componente desmontar antes de o listener existir.
		let remover;
		let cancelado = false;

		(async () => {
			const { Network } = await import('@capacitor/network');

			// A leitura inicial, porque o listener só dispara na MUDANÇA -- quem abre o app já em modo
			// avião não veria evento nenhum.
			const estado = await Network.getStatus();
			if (!cancelado) setOnline(estado.connected);

			const inscricao = await Network.addListener('networkStatusChange', ({ connected }) => {
				setOnline(connected);
			});

			if (cancelado) inscricao.remove();
			else remover = () => inscricao.remove();
		})();

		return () => {
			cancelado = true;
			remover?.();
		};
	}, []);

	return online;
}
